import { useLayoutEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { flushSync } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { Icon } from "@legal-500/legal500-design-system";
import { CmsEmptyState } from "@/components/ResearchCms";
import { findOrgEntry } from "../../utils/findOrgContentId";
import type { OrgEntryLookup } from "../../utils/findOrgContentId";
import type { SourceDataFirm } from "../../utils/sourceDataFirm";
import { KeyStatsBar } from "./KeyStatsBar";
import { L500LiveTab } from "./L500LiveTab";
import { OriginalSubmissionTab } from "./OriginalSubmissionTab";
import tabStyles from "./OriginalSubmissionTab.module.css";
import styles from "./SourceDataPanel.module.css";
import { TestimonialsTab } from "./TestimonialsTab";
import { useSourcePanelOpen } from "./useSourcePanelOpen";
import { useTabHighlights } from "./useTabHighlights";

export type SourceDataSource =
  | { kind: "pending" }
  | { kind: "error" }
  | { kind: "known"; docParseResultId: string | null }
  | { kind: "lookup"; lookup: OrgEntryLookup };

/** The page's names every testimonial card shows; the firm name is the panel's `title`. */
export type SourceDataPageNames = {
  editionName: string;
  rankingEntityName: string;
  bpaName: string;
};

type SourceDataPanelProps = {
  title: string;
  source: SourceDataSource;
  templateType: string | null;
  firm: SourceDataFirm;
  editionId: string;
  researchBpaId: string;
  names: SourceDataPageNames;
};

type TabId = "live" | "testimonials" | "original";

type PanelMotion = "opening" | "closing" | null;

const TABS: { id: TabId; label: string }[] = [
  { id: "live", label: "L500 Live" },
  { id: "testimonials", label: "Testimonials" },
  { id: "original", label: "Original Submission" },
];

const SETTLE_MARGIN_MS = 50;

function tabDomId(id: TabId): string {
  return `source-data-tab-${id}`;
}

function milliseconds(value: CSSNumberish | null | undefined): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function remainingAnimationMs(element: Element): number {
  if (typeof element.getAnimations !== "function") return 0;
  return element.getAnimations().reduce((longest, animation) => {
    const end = milliseconds(animation.effect?.getComputedTiming().endTime);
    return Math.max(longest, end - milliseconds(animation.currentTime));
  }, 0);
}

function remainingMoveMs(shell: HTMLElement): number {
  return [shell, ...shell.children].reduce(
    (longest, element) => Math.max(longest, remainingAnimationMs(element)),
    0,
  );
}

/**
 * Tracks an open or close move of the panel from the toggle until its transitions have ended. The
 * end is a timer taken from the transitions' own end time plus a margin, so a transition that never
 * advances (a throttled or background tab) cannot leave the panel stranded part-way. Reads the
 * shell and its direct children only: the loading spinner below them animates forever.
 */
function usePanelMotion() {
  const shellRef = useRef<HTMLDivElement>(null);
  const [motion, setMotion] = useState<PanelMotion>(null);

  useLayoutEffect(() => {
    if (motion === null) return;
    const remaining = shellRef.current ? remainingMoveMs(shellRef.current) : 0;
    if (remaining <= 0) {
      setMotion(null);
      return;
    }
    const timer = window.setTimeout(() => setMotion(null), remaining + SETTLE_MARGIN_MS);
    return () => window.clearTimeout(timer);
  }, [motion]);

  return { shellRef, motion, setMotion };
}

const TAB_IDS = TABS.map(tab => tab.id);

function SourceTabList({
  activeTab,
  onSelect,
}: {
  activeTab: TabId;
  onSelect: (id: TabId) => void;
}) {
  const tabRefs = useRef<Record<TabId, HTMLButtonElement | null>>({
    live: null,
    testimonials: null,
    original: null,
  });
  const highlights = useTabHighlights(TAB_IDS, activeTab);

  function moveFocus(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const step = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const next = TABS[(index + step + TABS.length) % TABS.length];
    onSelect(next.id);
    tabRefs.current[next.id]?.focus();
  }

  return (
    <div
      ref={highlights.registerList}
      role="tablist"
      aria-label="Source data tabs"
      className={styles.tabs}
    >
      <span
        aria-hidden="true"
        className={styles.highlightSelected}
        data-highlight="selected"
        data-tab={highlights.selectedHighlight.tab}
        data-instant={highlights.selectedHighlight.instant ? "" : undefined}
        style={highlights.selectedHighlight.style}
      />
      {TABS.map((tab, index) => (
        <button
          key={tab.id}
          ref={element => {
            tabRefs.current[tab.id] = element;
            highlights.registerTab(tab.id, element);
          }}
          type="button"
          role="tab"
          id={tabDomId(tab.id)}
          aria-selected={activeTab === tab.id}
          aria-controls="source-data-tabpanel"
          tabIndex={activeTab === tab.id ? 0 : -1}
          className={styles.tab}
          onClick={() => onSelect(tab.id)}
          onKeyDown={event => moveFocus(event, index)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function LoadingOriginalSubmission() {
  return <CmsEmptyState loading message="Loading original submission" />;
}

function LoadFailed() {
  return (
    <div className={tabStyles.tab}>
      <div role="alert" className={tabStyles.error}>
        The original submission could not be loaded. Try again shortly.
      </div>
    </div>
  );
}

function LookedUpSubmission({
  lookup,
  templateType,
}: {
  lookup: OrgEntryLookup;
  templateType: string | null;
}) {
  const entry = useQuery({
    queryKey: ["research-source-data-entry", lookup],
    queryFn: () => findOrgEntry({ ...lookup, liveOnly: true }),
  });
  if (entry.isLoading) return <LoadingOriginalSubmission />;
  if (entry.isError) return <LoadFailed />;
  return (
    <OriginalSubmissionTab
      docParseResultId={entry.data?.docParseResultId ?? null}
      open
      templateType={templateType}
    />
  );
}

function OriginalSubmissionBody({
  source,
  templateType,
}: {
  source: SourceDataSource;
  templateType: string | null;
}) {
  if (source.kind === "pending") return <LoadingOriginalSubmission />;
  if (source.kind === "error") return <LoadFailed />;
  if (source.kind === "lookup") {
    return <LookedUpSubmission lookup={source.lookup} templateType={templateType} />;
  }
  return (
    <OriginalSubmissionTab
      docParseResultId={source.docParseResultId}
      open
      templateType={templateType}
    />
  );
}

export function SourceDataPanel({
  title,
  source,
  templateType,
  firm,
  editionId,
  researchBpaId,
  names,
}: SourceDataPanelProps) {
  const { open, setOpen } = useSourcePanelOpen();
  const { shellRef, motion, setMotion } = usePanelMotion();
  const [activeTab, setActiveTab] = useState<TabId>("original");
  const railRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);

  function changeOpen(next: boolean) {
    flushSync(() => {
      setMotion(next ? "opening" : "closing");
      setOpen(next);
    });
    const target = next ? panelRef.current : railRef.current;
    target?.focus({ preventScroll: true });
  }

  return (
    <div ref={shellRef} className={styles.shell} data-open={open} data-moving={motion !== null}>
      {!open && (
        <button
          ref={railRef}
          type="button"
          className={styles.rail}
          aria-expanded={false}
          onClick={() => changeOpen(true)}
        >
          <span aria-hidden="true">
            <Icon icon="chevron-left" size="small" />
          </span>
          <span className={styles.railLabel}>Source Data</span>
        </button>
      )}
      {(open || motion !== null) && (
        <aside
          ref={panelRef}
          id="source-data-panel"
          className={styles.panel}
          aria-label="Source data"
          aria-hidden={open ? undefined : true}
          inert={!open}
          tabIndex={-1}
        >
          <div className={styles.chrome}>
            <div className={styles.head}>
              <div className={styles.headText}>
                <p className={styles.eyebrow}>Source data</p>
                <h2 className={styles.title}>{title}</h2>
              </div>
              <button
                type="button"
                className={styles.collapse}
                aria-label="Collapse source data panel"
                aria-expanded
                aria-controls="source-data-panel"
                onClick={() => changeOpen(false)}
              >
                <Icon icon="chevron-right" size="small" />
              </button>
            </div>
            <KeyStatsBar
              firm={firm}
              editionId={editionId}
              researchBpaId={researchBpaId}
              enabled={open}
            />
            <SourceTabList activeTab={activeTab} onSelect={setActiveTab} />
          </div>
          <div
            role="tabpanel"
            id="source-data-tabpanel"
            aria-labelledby={tabDomId(activeTab)}
            className={styles.body}
          >
            {activeTab === "live" && (
              <L500LiveTab
                firm={firm}
                editionId={editionId}
                researchBpaId={researchBpaId}
                enabled={open}
              />
            )}
            {activeTab === "testimonials" && (
              <TestimonialsTab
                firm={firm}
                editionId={editionId}
                researchBpaId={researchBpaId}
                names={{ ...names, firmName: title }}
                enabled={open}
              />
            )}
            {activeTab === "original" && (
              <OriginalSubmissionBody source={source} templateType={templateType} />
            )}
          </div>
        </aside>
      )}
    </div>
  );
}
